import {
  GoogleMap,
  GoogleMapProps,
  InfoWindow,
  Polygon
} from '@react-google-maps/api'
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import { useGoogleMaps } from 'src/providers/GoogleMapsProvider'

import { GeoData } from 'src/types/map'

import { ClusterPoints } from '../MultiPointMap/ClusterPoints'
import { PolygonMapControls } from './PolygonMapControls'
import {
  DEFAULT_CENTER,
  DEFAULT_MAP_PROPS,
  checkIsIntersecting,
  NEW_ZEALAND_BOUNDS,
  polygonColors
} from './helpers'

export type PolygonAndArea = { area: number; data: google.maps.LatLngLiteral[] }

export type PolygonMapReturnData = {
  polygons: PolygonAndArea[]
  totalArea: number
}

export type PolyData = {
  groupKey: string
  title: string
  content: ReactNode | ((polydata: PolygonAndArea) => ReactNode)
  polygonData: PolygonMapReturnData
}

export type PolygonMapProps = {
  /**
   * GEO to center the map
   */
  mapCenter?: GeoData
  /**
   * Callback when polygons on the map change
   * @param data Polygon data, will be null if there's an error
   * @param error Description of the error
   * @returns
   */
  onChange?: (data: PolygonMapReturnData | null, error?: string) => void
  /**
   * Default zoom level
   */
  zoom: number
  /**
   * Indicates that the map should be constrained to some bounds and zoom level
   */
  constrainMap?: boolean
  /**
   * Disables editing on polygons if true
   */
  readOnly?: boolean
  /**
   * Polygon data which will be editable
   */
  polygonData?: PolygonMapReturnData
  /**
   * Parsed readonly polygon data
   */
  readOnlyPolyData?: PolyData[]
  /**
   * Additional points data to display
   */
  readOnlyPointData?: GeoData[]
  /**
   * Indicates whether or not to validate polygon intersection
   */
  allowGroupIntersect?: boolean
  /**
   * Classnames to apply to the map container div
   */
  className?: string
  /**
   * The groupkey which will be used to validate agains intersecting polygons
   */
  groupKey?: string
}
export const PolygonMap = ({
  mapCenter,
  zoom,
  constrainMap,
  readOnly,
  polygonData,
  readOnlyPolyData = [],
  readOnlyPointData,
  className = '',
  onChange,
  allowGroupIntersect = true,
  groupKey
}: PolygonMapProps) => {
  const { isLoaded } = useGoogleMaps()
  const [localPolygons, setLocalPolygons] = useState<
    google.maps.LatLngLiteral[][]
  >(polygonData?.polygons?.map(p => p.data) || [])
  const [intersectingIndex, setIntersectingIndex] = useState<number | null>(
    null
  )
  const [infoWindowState, setInfoWindowState] = useState<{
    visible: boolean
    position: google.maps.LatLng
    content: ReactNode
    title: string
  } | null>(null)
  const infoWindowRef = useRef<InfoWindow>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const polygonRefs = useRef<(Polygon | null)[]>([])
  const readOnlyPolygonRefs = useRef<(Polygon | null)[]>([])
  const [error, setError] = useState('')
  const [isAdding, setIsAdding] = useState(false)
  const [selectedPolygonIndex, setSelectedPolygonIndex] = useState<
    number | null
  >(null)

  const finalMapProps = useMemo<GoogleMapProps>(() => {
    if (constrainMap) {
      return {
        ...DEFAULT_MAP_PROPS,
        options: {
          ...DEFAULT_MAP_PROPS.options,

          minZoom: 5,
          maxZoom: 15,
          restriction: {
            latLngBounds: NEW_ZEALAND_BOUNDS
          }
        }
      }
    }
    return DEFAULT_MAP_PROPS
  }, [constrainMap])

  const colorSeasonMap = useMemo(() => {
    return readOnlyPolyData.reduce((acc, curr) => {
      if (curr.groupKey in acc) {
        return acc
      }
      return {
        ...acc,
        [curr.groupKey]: polygonColors[Object.keys(acc).length] || ''
      }
    }, {} as Record<string, string>)
  }, [readOnlyPolyData])

  /**
   * Function to check whether the currently selected polygon intersects
   * with any of the other polygons.  This function will update the
   * intersecting index state which is used to indicate that, and
   * used to return the polygon data accordingly when persisting
   */
  const checkIntersection = useCallback(() => {
    if (allowGroupIntersect) {
      setIntersectingIndex(null)
      return null
    }
    if (selectedPolygonIndex !== null) {
      const polygonsToCheck = polygonRefs.current
        .map(p => p?.polygon!)
        .concat(readOnlyPolygonRefs.current.map(p => p?.polygon!))
        .filter(Boolean)
      const isIntersecting = checkIsIntersecting(
        selectedPolygonIndex,
        polygonsToCheck
      )
      const intersectingIdx = isIntersecting ? selectedPolygonIndex : null
      setIntersectingIndex(intersectingIdx)
      return intersectingIdx
    }
    return null
  }, [polygonRefs, selectedPolygonIndex, allowGroupIntersect])

  /**
   * This function will do the work of calculating the area of all polygons and
   * call the onChange callback with the polygon data and calculated area
   */
  const persist = useCallback(
    (
      polydata: google.maps.LatLngLiteral[][],
      intersectingIdx: number | null
    ) => {
      setError('')
      let errorMessage = ''

      if (intersectingIdx !== null) {
        errorMessage =
          'Polygons should not overlap with another in the same season'
      }

      const polygons = polydata.reduce(
        (acc: PolygonMapReturnData, curr: google.maps.LatLngLiteral[]) => {
          const polygonArea = google.maps.geometry.spherical.computeArea(curr)
          const totalArea = acc.totalArea + polygonArea

          return {
            polygons: [
              ...acc.polygons,
              {
                data: curr,
                area: polygonArea
              }
            ],
            totalArea
          }
        },
        { polygons: [], totalArea: 0 }
      )
      if (polygons.polygons.some(polygon => polygon.data.length < 4)) {
        errorMessage = 'Polygons should have at least four points'
      }
      if (errorMessage) {
        setError(errorMessage)
        if (onChange) {
          onChange(null)
        }
        return
      }

      if (onChange) {
        onChange(polygons)
      }
    },
    [onChange]
  )

  /**
   * This useEffect is responsible for setting up the event listeners on the
   * polygons, for when a user right clicks on a node to remove it
   */
  useEffect(() => {
    if (!polygonRefs.current) return

    const deleteNode = (ev: google.maps.PolyMouseEvent, index: number) => {
      if (
        (selectedPolygonIndex === null && !isAdding) ||
        ev.vertex === null ||
        ev.vertex === undefined ||
        !polygonRefs.current[index]?.polygon
      ) {
        return
      }

      polygonRefs.current[index].polygon.getPath().removeAt(ev.vertex)
      const intersectingIdx = checkIntersection()
      const newpath = polygonRefs.current[index].polygon
        .getPath()
        .getArray()
        .map(p => ({ lat: p.lat(), lng: p.lng() }))
      const updatedPolygons = localPolygons.filter(p => p.length)
      updatedPolygons.splice(index, 1, newpath)
      setLocalPolygons(updatedPolygons)
      persist(updatedPolygons, intersectingIdx)
    }

    const listeners = polygonRefs.current
      .filter(pRef => !!pRef?.polygon)
      .map((pRef, index) => {
        return google.maps.event.addListener(
          pRef?.polygon!,
          'rightclick',
          (ev: google.maps.PolyMouseEvent) => {
            deleteNode(ev, index)
          }
        )
      })

    return () => {
      listeners.forEach(listener => {
        google.maps.event.removeListener(listener)
      })
    }
  }, [
    isAdding,
    polygonRefs,
    selectedPolygonIndex,
    persist,
    localPolygons,
    checkIntersection
  ])

  /**
   * Function responsible for doing a couple of things:
   * - when the user creates a node, it will push the coordinates of the mouse event to
   * the new polygon
   * - default the selected polygon index if not in 'adding' state
   */
  const handleMapClick = useCallback(
    ({ latLng }: google.maps.MapMouseEvent) => {
      setInfoWindowState(null)
      if (isAdding && selectedPolygonIndex !== null && latLng) {
        polygonRefs.current[selectedPolygonIndex]?.polygon
          ?.getPath()
          .push(latLng)

        checkIntersection()

        const path = polygonRefs.current[selectedPolygonIndex]?.polygon
          ?.getPath()
          .getArray()
          .map(p => ({ lat: p.lat(), lng: p.lng() }))

        if (!path) {
          return
        }

        const newArray = localPolygons.filter(p => p.length)
        newArray.splice(selectedPolygonIndex, 1, path)
        setLocalPolygons(newArray)

        return
      }
      setSelectedPolygonIndex(null)
    },
    [isAdding, selectedPolygonIndex, checkIntersection, localPolygons]
  )

  /**
   * Sets the currently selected polygon index to track what we edit or delete
   */
  const handlePolygonClick = useCallback(
    (index: number) => {
      if (!isAdding) {
        setSelectedPolygonIndex(index)
      }
    },
    [isAdding]
  )

  /**
   * When the map loads, we want to center it based on the existing geo or polygon data
   * @param map Google map instance
   */
  const handleOnLoad = useCallback(
    (map: google.maps.Map) => {
      const bounds = new google.maps.LatLngBounds()
      const allPolydata = (polygonData?.polygons || [])
        .flatMap(p => p.data)
        .concat(
          ...readOnlyPolyData.flatMap(ro =>
            ro.polygonData?.polygons?.flatMap(pl => pl.data)
          )
        )

      allPolydata?.forEach(p => {
        if (!p) return
        bounds.extend(p)
      })

      // Add readOnlyPointData to bounds
      readOnlyPointData?.forEach(point => {
        if (
          point &&
          typeof point.lat === 'number' &&
          typeof point.lng === 'number'
        ) {
          bounds.extend({ lat: point.lat, lng: point.lng })
        }
      })

      if (bounds.isEmpty()) {
        bounds.extend(
          mapCenter?.place_id
            ? { lat: mapCenter.lat, lng: mapCenter.lng }
            : DEFAULT_CENTER
        )
      }
      if (mapCenter?.place_id) {
        map.setCenter(mapCenter)
      } else {
        map.fitBounds(bounds)
        map.setCenter(bounds.getCenter())
      }
    },
    [mapCenter, polygonData, readOnlyPolyData, readOnlyPointData]
  )
  /**
   * When we're in 'adding' state, default the currently selected polygon index and remove the last added polygon
   */
  const handleCancel = useCallback(() => {
    if (!isAdding) {
      return
    }
    setSelectedPolygonIndex(null)
    setLocalPolygons(curr => curr.slice(0, curr.length - 1))
    setIsAdding(false)
  }, [isAdding])

  /**
   * Default the isAdding and selected polygon index values, then persist
   */
  const handleSave = useCallback(() => {
    if (localPolygons.some(poly => poly.length < 4)) {
      setError('Polygons should have at least four points')
      return
    }
    setIsAdding(false)
    setSelectedPolygonIndex(null)
    persist(
      localPolygons.filter(p => p.length),
      intersectingIndex
    )
  }, [persist, localPolygons, intersectingIndex])

  /**
   * Make sure we don't already have something selected, then set the currently selected polygon index,
   * set isAdding to true and insert an empty array to locally tracked polygons.
   */
  const handleNew = useCallback(() => {
    if (selectedPolygonIndex !== null) {
      return
    }
    setIsAdding(true)
    setSelectedPolygonIndex(localPolygons.length)
    setLocalPolygons(curr => [...curr, []])
  }, [selectedPolygonIndex, localPolygons])

  /**
   * when we have a selected polygon index, we remove that index from state and the polygon refs,
   * then persist
   */
  const handleDelete = useCallback(() => {
    if (selectedPolygonIndex === null) {
      return
    }
    const newPolygons = localPolygons.filter(p => p.length)
    newPolygons.splice(selectedPolygonIndex, 1)
    setLocalPolygons(newPolygons)
    polygonRefs.current = polygonRefs.current.filter(
      (_, idx) => idx !== selectedPolygonIndex
    )
    setSelectedPolygonIndex(null)
    persist(newPolygons, intersectingIndex)
  }, [selectedPolygonIndex, localPolygons, persist, intersectingIndex])

  const handleEdit = useCallback(
    (ev: google.maps.MapMouseEvent) => {
      if ((ev.domEvent as any).button === 2 || selectedPolygonIndex === null) {
        return
      }
      const intersectingIdx = checkIntersection()

      const path = polygonRefs.current[selectedPolygonIndex]?.polygon
        ?.getPath()
        .getArray()
        .map(p => ({ lat: p.lat(), lng: p.lng() }))
      if (!path) {
        return
      }
      const updatedPolygons = localPolygons.filter(p => p.length)
      updatedPolygons.splice(selectedPolygonIndex, 1, path)
      setLocalPolygons(updatedPolygons)
      persist(updatedPolygons, intersectingIdx)
    },
    [
      polygonRefs,
      selectedPolygonIndex,
      checkIntersection,
      localPolygons,
      persist
    ]
  )

  /**
   * To make the UX a bit more intuitive, and removing the need for the user to click save,
   * we call this method when the containing div looses focus. it will ensure we are in a correct
   * state before calling persist.
   */
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      setInfoWindowState(null)
      if (localPolygons.some(poly => poly.length < 4)) {
        return
      }
      if (selectedPolygonIndex !== null || isAdding) {
        if (!event.target) return
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setSelectedPolygonIndex(null)
          setIsAdding(false)

          persist(
            localPolygons.filter(p => p.length),
            intersectingIndex
          )
        }
      }
    },
    [intersectingIndex, isAdding, localPolygons, persist, selectedPolygonIndex]
  )

  const handleReadonlyClick = (
    position: google.maps.LatLng,
    polydata: PolyData,
    polygon: PolygonAndArea
  ) => {
    setInfoWindowState({
      visible: true,
      content:
        typeof polydata.content === 'function'
          ? polydata.content(polygon)
          : polydata.content,
      position,
      title: polydata.title
    })
  }

  /**
   * Adding an event listener to the document to improve UX.
   */
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [handleClickOutside])

  if (!isLoaded) return null

  return (
    <div ref={containerRef} className={className}>
      <GoogleMap
        {...finalMapProps}
        onClick={handleMapClick}
        zoom={zoom}
        onLoad={handleOnLoad}
      >
        {infoWindowState?.visible ? (
          <InfoWindow
            options={{
              headerContent: infoWindowState.title
            }}
            onCloseClick={() => setInfoWindowState(null)}
            ref={infoWindowRef}
            position={infoWindowState.position}
          >
            {infoWindowState.content}
          </InfoWindow>
        ) : null}
        {readOnly ? null : (
          <PolygonMapControls
            isAdding={isAdding}
            hasItemSelected={selectedPolygonIndex !== null}
            onCancelClick={handleCancel}
            onSaveClick={handleSave}
            onNewClick={handleNew}
            onDeleteClick={handleDelete}
            error={error}
          />
        )}
        {localPolygons.map((localPolygon, idx) => {
          const isSelected = selectedPolygonIndex === idx
          let color = 'lightgreen'
          if (intersectingIndex === idx || localPolygon.length < 4) {
            color = 'red'
          }
          return (
            <Polygon
              key={`polygon_${idx}`}
              ref={el => {
                polygonRefs.current[idx] = el
              }}
              path={localPolygon}
              onMouseUp={handleEdit}
              onDragEnd={handleEdit}
              onClick={() => {
                handlePolygonClick(idx)
              }}
              editable={
                isSelected ||
                (isAdding && selectedPolygonIndex === localPolygons.length)
              }
              draggable={isSelected}
              options={{
                zIndex:
                  selectedPolygonIndex === idx || idx === intersectingIndex
                    ? 10
                    : 0,
                strokeWeight: 2,
                fillColor: color,
                fillOpacity: isSelected ? 0.5 : 0.3,
                strokeColor: color
              }}
            />
          )
        })}
        {readOnlyPolyData
          .filter(rop => !!rop.polygonData?.polygons)
          .flatMap(rop =>
            rop.polygonData?.polygons.map(p => ({
              polygon: p,
              polydata: rop
            }))
          )
          .map((p, pidx) => (
            <Polygon
              ref={el => {
                if (groupKey === p.polydata.groupKey) {
                  readOnlyPolygonRefs.current[pidx] = el
                }
              }}
              key={`read_only_poly_${pidx}`}
              data-testid={`read_only_poly_${pidx}`}
              onClick={ev => {
                if (!ev.latLng) return
                handleReadonlyClick(ev.latLng, p.polydata, p.polygon)
              }}
              path={p.polygon.data}
              options={{
                fillColor: colorSeasonMap[p.polydata.groupKey],
                fillOpacity: 0.2,
                strokeColor: colorSeasonMap[p.polydata.groupKey],
                editable: false
              }}
            />
          ))}
        <ClusterPoints pointData={readOnlyPointData || []} clustered />
      </GoogleMap>
    </div>
  )
}
